import type { ProductOptionalFieldName } from '@inter-ikea-kompis/core/types'

import type { ItemId, ProductData } from '@sofa-platform/types'

type Fields = '*' | 'priceClass' | ProductOptionalFieldName

type GetApplicationProductsParams = {
  appId: string
} & GetProductsParams

type GetProductsParams = {
  basePath: string
  dexfApiKey: string
  fields?: Fields[]
}

export async function getApplicationProducts(params: GetApplicationProductsParams) {
  const fields = (params.fields ?? ['*']).join(',')
  const url = `${params.basePath}?filter.appId=${params.appId}&fields=${fields}&filter.valid=true`

  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'dexf-api-key': params.dexfApiKey,
    },
    method: 'GET',
  })
  const data = (await response.json()) as { data: ProductData[] }
  return data.data
}

export async function getProducts(items: ItemId[], params: GetProductsParams) {
  if (!params.basePath) throw new Error('DEXF Webplanner service URL is not defined in the settings.')
  if (!params.dexfApiKey) throw new Error('DEXF Webplanner API key is not defined in the settings.')
  const arr = chunkArray(items, 50)
  const fields = (params.fields ?? ['*']).join(',')

  const baseUrl = `${params.basePath}?fields=${fields}&filter.valid=true`

  const promises = arr.map((chunk) => {
    const itemIds = chunk.join(',')
    const url = `${baseUrl}&filter.itemId=${itemIds}`

    return fetch(url, {
      credentials: 'same-origin',
      headers: {
        'dexf-api-key': params.dexfApiKey,
      },
      method: 'GET',
    })
  })
  const parts = await Promise.all(promises)

  const results: ProductData[][] = await Promise.all(
    parts.map((response) => response.json().then((res) => res.data) as Promise<ProductData[]>),
  )
  return results.flat()
}

function chunkArray<T extends string>(array: T[], chunkSize: number): T[][] {
  return array.reduce<T[][]>((result, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize)
    result[chunkIndex] = result[chunkIndex] || []
    result[chunkIndex].push(item)
    return result
  }, [])
}
